/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import { createRouter, createWebHistory } from 'vue-router/auto'
import { setupLayouts } from 'virtual:generated-layouts'
import { routes } from 'vue-router/auto-routes'
import { useUserStore } from '@/stores/userStore'
import { useRouteGuard } from '@/composables/useRouteGuard'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: setupLayouts(routes),
})

// Route guards for management-only pages
router.beforeEach(async (to, from, next) => {
  // check if this is the resource planning or base load page
  if (to.path === '/resource-planning') {
    const userStore = useUserStore()
    const routeGuard = useRouteGuard()

    try {
      // ensure user permissions are loaded
      const userLoaded = await routeGuard.ensureUserPermissions()

      if (!userLoaded) {
        // if user info couldn't be loaded, there might be an auth issue
        console.error('Could not load user information for route guard')
        // redirect to login/home instead of previous page to avoid loops
        next('/')
        return
      }

      // wait for user store to finish loading if it's still in progress
      while (userStore.isLoadingUserInfo) {
        await new Promise(resolve => setTimeout(resolve, 50))
      }

      // check if user has management permissions
      if (!routeGuard.requireManagementAccess()) {
        // redirect to no-access page for unauthorized users
        next('/no-access')
        return
      }
    } catch (error) {
      console.error('Route guard error:', error)
      next('/')
      return
    }
  }

  next()
})

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('Reloading page to fix dynamic import error')
      localStorage.setItem('vuetify:dynamic-reload', 'true')
      location.assign(to.fullPath)
    } else {
      console.error('Dynamic import error, reloading page did not fix it', err)
    }
  } else {
    console.error(err)
  }
})

router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload')
})

export default router
